import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import FormContext from 'dpl/shared/form/utils/context';
import useForm from 'dpl/shared/form/hooks/useForm';
import QuestionLayout from 'dpl/quiz/components/QuestionLayout';
import MultiPhotoUpload from 'dpl/form/components/MultiPhotoUpload';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import useToastNotifications from 'dpl/shared/hooks/useToastNotifications';
import { TOAST_NOTIFICATIONS_TYPES } from 'dpl/shared/constants/shared';
import { RESCUABLE_STATUS_CODES } from 'dpl/shared/fetching/utils/constants';
import Icon from 'dpl/common/components/Icon';
import {
  testimonialQuizMutationDefinition,
  testimonialQuizEditPhotosDefinition
} from 'dpl/testimonials/utils/queryDefinitions';
import TptConnectError from 'dpl/shared/fetching/utils/TptConnectError';
import { isEmpty } from 'dpl/shared/utils/object';

/*
The TestimonialPhotoUpload component is under quiz however we are bypassing quiz logic here.
We are directly making a request to testimonials to update the
associated photographs and no quiz submission is necessary.
To bypass quiz formState, we have a nested formState within TestimonialPhotoUpload.
The MultiPhotoUpload's formContext will use this most recent formState.

Photos are persisted as soon as each upload lands, not when Continue is clicked. The
shared quiz footer can advance this section without going through Continue (see
QuizLayout#handleNextClick and FullPageQuizMobileNavigation), so a save deferred to
Continue is silently skipped on mobile and the photos are lost. See SUPPORT-1117.
*/

const name = 'photos';
const SAVE_TIMEOUT_MS = 30000;

// with ignoreRescueOn below, rescuable failures resolve instead of throwing --
// a save succeeded only if its response says so
function didSaveSucceed(result) {
  return !!result?.response?.ok;
}
export default function TestimonialPhotoUpload({
  title,
  subtitle,
  canAdvance,
  onNextClick
}) {
  const { testimonialId, mediaCategoryId } = useSelector(
    ({ server }) => server
  );

  const { pushToastNotification } = useToastNotifications();

  const { isFetching, value } = useResourceQuery(
    testimonialQuizEditPhotosDefinition(testimonialId)
  );

  const { mutateAsync: updateTestimonial } = useResourceMutation({
    ...testimonialQuizMutationDefinition(testimonialId),
    // rescuable statuses (500s etc.) normally toast globally and return a
    // promise that never settles, which would deadlock the save queue and
    // Continue. Opt out and surface those failures through the queue instead.
    // Scoped here rather than in the shared definition so the quiz submit
    // mutation keeps the platform behavior.
    createFetchDataConfig: { ignoreRescueOn: RESCUABLE_STATUS_CODES },
    updateStrategy: (prev, next) => (didSaveSucceed(next) ? next : prev)
  });

  const { setValue, formErrors, formState, contextValue } = useForm({
    defaultFormState: { [name]: [] }
  });

  const photos = formState[name];

  // what we've already handed to the server (signed ids for creates, row ids
  // for destroys), so overlapping saves can't repeat work. Entries are removed
  // again when a save fails, to leave them eligible for a retry.
  const sentKeysRef = useRef(new Set());
  // saves run one at a time so concurrent uploads can't race acts_as_list positions
  const saveQueueRef = useRef(Promise.resolve());
  const appliedServerPhotosRef = useRef(null);
  const didBlockContinueRef = useRef(false);
  const isMountedRef = useRef(true);

  const [inFlightSaves, setInFlightSaves] = useState(0);
  const [isUploadPending, setIsUploadPending] = useState(false);

  const isSaving = inFlightSaves > 0;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  function getUnsavedAttributes() {
    const creates = photos
      .filter(
        photo => photo.file && !photo.id && !sentKeysRef.current.has(photo.file)
      )
      .map(({ file, media_category_id: photoMediaCategoryId }) => ({
        file,
        media_category_id: photoMediaCategoryId
      }));

    const destroys = photos
      .filter(
        photo =>
          photo.id && photo._destroy && !sentKeysRef.current.has(photo.id)
      )
      .map(({ id }) => ({ id, _destroy: true }));

    return [...creates, ...destroys];
  }

  function markSent(attributes) {
    attributes.forEach(({ file, id }) => sentKeysRef.current.add(file ?? id));
  }

  function markUnsent(attributes) {
    attributes.forEach(({ file, id }) =>
      sentKeysRef.current.delete(file ?? id)
    );
  }

  function savePhotos() {
    const attributes = getUnsavedAttributes();

    if (!attributes.length) {
      return saveQueueRef.current;
    }

    markSent(attributes);
    setInFlightSaves(n => n + 1);

    saveQueueRef.current = saveQueueRef.current
      .then(() =>
        updateTestimonial({
          body: { testimonial: { photographs_attributes: attributes } },
          // a request that never comes back would strand the queue forever;
          // optional-called since older browsers (and jsdom) lack timeout
          signal: AbortSignal.timeout?.(SAVE_TIMEOUT_MS)
        })
      )
      .then(result => {
        if (!didSaveSucceed(result)) {
          throw new TptConnectError(
            `Server responded with ${result?.response?.status}`,
            { response: result?.response, responseBody: result?.data }
          );
        }

        didBlockContinueRef.current = false;
      })
      .catch(err => {
        markUnsent(attributes);

        window.bugsnagClient?.notify(err, {
          metaData: { testimonialId, photographs_attributes: attributes }
        });

        pushToastNotification({
          type: TOAST_NOTIFICATIONS_TYPES.ERROR,
          message: 'We couldn’t save your photos. Please try again.'
        });
      })
      .finally(() => {
        if (isMountedRef.current) {
          setInFlightSaves(n => n - 1);
        }
      });

    return saveQueueRef.current;
  }

  const hasUnsavedPhotos = !!getUnsavedAttributes().length;

  // the server is the source of truth, but don't clobber photos that haven't
  // made it there yet. Save responses carry the persisted list (see
  // updateStrategy), so ids land here without a refetch round trip; the
  // identity check keeps re-runs from re-applying a list already applied.
  useEffect(() => {
    if (isFetching || isSaving || hasUnsavedPhotos) return;

    const serverPhotos = value.testimonial.photographs_attributes;
    if (serverPhotos === appliedServerPhotosRef.current) return;

    appliedServerPhotosRef.current = serverPhotos;

    // a photo deleted while its create PUT was in flight had no id to send a
    // _destroy with, so useFormState spliced it out locally. Its server row
    // comes back here carrying the signed id we sent -- recognize it by its
    // absence from the local list and destroy it instead of showing it again.
    const localSignedIds = new Set(photos.map(photo => photo.file));
    setValue(
      name,
      serverPhotos.map(photo =>
        photo.file &&
        sentKeysRef.current.has(photo.file) &&
        !localSignedIds.has(photo.file)
          ? { ...photo, _destroy: true }
          : photo
      )
    );
  }, [isFetching, isSaving, hasUnsavedPhotos, value]);

  useEffect(() => {
    savePhotos();
  }, [photos]);

  async function handleNextClick() {
    // retries anything a previous save failed on
    await savePhotos();

    // block once to allow a retry, but don't strand the buyer behind a save
    // that keeps failing -- the next failed attempt continues on
    if (getUnsavedAttributes().length && !didBlockContinueRef.current) {
      didBlockContinueRef.current = true;
      return;
    }

    onNextClick();
  }

  return (
    <div className="TestimonialPhotoUpload min-h-100 flex flex-column justify-center items-center">
      <QuestionLayout title={title} subtitle={subtitle}>
        <div className="mb8">
          <FormContext.Provider value={contextValue}>
            <MultiPhotoUpload
              name={name}
              mediaCategoryId={mediaCategoryId}
              limit={8}
              onPendingStatusChange={setIsUploadPending}
            />
          </FormContext.Provider>
        </div>
        <div className="tc">
          <button
            type="button"
            onClick={handleNextClick}
            className="button button--primary button--large pv3 inline-flex fw-medium items-center"
            disabled={
              isSaving || isUploadPending || !canAdvance || !isEmpty(formErrors)
            }
          >
            Continue
            <Icon className="ml1" name="fat-arrow-right" />
          </button>
        </div>
      </QuestionLayout>
    </div>
  );
}

TestimonialPhotoUpload.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  canAdvance: PropTypes.bool,
  onNextClick: PropTypes.func.isRequired
};

TestimonialPhotoUpload.defaultProps = {
  subtitle: '',
  canAdvance: true
};
